import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Loader2, Users as UsersIcon, Pencil } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  customer: "顧客",
  vendor: "仕入先",
  both: "両方",
};

const emptyCustomer = () => ({
  name: "",
  contact_name: "",
  email: "",
  phone: "",
  address: "",
  type: "customer",
  notes: "",
});

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyCustomer());

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyCustomer());
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "",
      contact_name: c.contact_name || "",
      email: c.email || "",
      phone: c.phone || "",
      address: c.address || "",
      type: c.type || "customer",
      notes: c.notes || "",
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error("名称を入力してください");
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/customers/${editingId}`, form);
        toast.success("取引先を更新しました");
      } else {
        await api.post("/customers", form);
        toast.success("取引先を登録しました");
      }
      setOpen(false);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!confirm("この取引先を削除しますか？")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("削除しました");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">取引先</div>
          <h1 className="mt-1 font-display font-bold text-3xl tracking-tight text-stone-900">取引先管理</h1>
          <p className="mt-1 text-sm text-stone-500">顧客と仕入先の情報を一元管理</p>
        </div>
        <button
          onClick={openCreate}
          data-testid="customer-create-button"
          className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-emerald-950"
        >
          <Plus className="h-4 w-4" /> 取引先を追加
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-display">{editingId ? "取引先を編集" : "取引先を追加"}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-sm">名称</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="customer-name-input" placeholder="株式会社○○" />
              </div>
              <div>
                <Label className="text-sm">担当者名</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5" data-testid="customer-contact-input" />
              </div>
              <div>
                <Label className="text-sm">区分</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="customer-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="customer">顧客</SelectItem>
                    <SelectItem value="vendor">仕入先</SelectItem>
                    <SelectItem value="both">両方</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">メール</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="customer-email-input" />
              </div>
              <div>
                <Label className="text-sm">電話</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" data-testid="customer-phone-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-sm">住所</Label>
                <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" data-testid="customer-address-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-sm">メモ</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="customer-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <button onClick={() => setOpen(false)} className="rounded-md border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">キャンセル</button>
              <button
                onClick={handleSave}
                disabled={submitting}
                data-testid="customer-submit-button"
                className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />} 保存する
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-emerald-900" /></div>
        ) : customers.length === 0 ? (
          <div className="p-10 text-center text-stone-500">
            <UsersIcon className="h-10 w-10 mx-auto mb-3 text-stone-300" strokeWidth={1.25} />
            取引先が登録されていません
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-stone-500 uppercase tracking-wider bg-stone-50/50 border-b border-stone-200">
                  <th className="px-6 py-3 text-left font-semibold">名称</th>
                  <th className="px-6 py-3 text-left font-semibold">担当者</th>
                  <th className="px-6 py-3 text-left font-semibold">区分</th>
                  <th className="px-6 py-3 text-left font-semibold">メール</th>
                  <th className="px-6 py-3 text-left font-semibold">電話</th>
                  <th className="px-6 py-3 text-right font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`customer-row-${c.id}`}>
                    <td className="px-6 py-3 font-medium text-stone-900">{c.name}</td>
                    <td className="px-6 py-3 text-stone-700">{c.contact_name || "—"}</td>
                    <td className="px-6 py-3">
                      <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
                        {TYPE_LABELS[c.type] || "顧客"}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-stone-500">{c.email || "—"}</td>
                    <td className="px-6 py-3 text-stone-500">{c.phone || "—"}</td>
                    <td className="px-6 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEdit(c)}
                          data-testid={`customer-edit-${c.id}`}
                          className="h-8 w-8 rounded-md hover:bg-stone-100 inline-flex items-center justify-center text-stone-500 hover:text-stone-900"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(c.id)}
                          data-testid={`customer-delete-${c.id}`}
                          className="h-8 w-8 rounded-md hover:bg-orange-50 inline-flex items-center justify-center text-stone-400 hover:text-orange-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
